import type { UrlValidationConfig } from '../../types';

/**
 * Converts a glob-like pattern to a regular expression.
 * Supports * (single segment wildcard) and ** (multi-segment wildcard).
 *
 * @param pattern - A glob-like pattern (e.g., 'trello.com/c/**', 'atlassian.net/browse/*')
 * @returns RegExp - Regular expression that matches the pattern
 *
 * @example
 * patternToRegex('trello.com/c/**') // Matches trello.com/c/abc/id
 * patternToRegex('jira.com/browse/*') // Matches jira.com/browse/ABC-123 but not jira.com/browse/a/b
 */
const patternToRegex = (pattern: string): RegExp => {
	const escaped = pattern
		.replace(/[.+?^${}()|[\]\\]/g, '\\$&') // Escape special chars
		.replace(/\*\*/g, '__MULTI__') // Placeholder for **
		.replace(/\*/g, '[^/]*') // Single * → match non-slash
		.replace(/__MULTI__/g, '.*'); // ** → match anything

	return new RegExp(`^${escaped}$`);
};

/**
 * Checks if a URL matches any of the allowed URL patterns.
 *
 * @param url - The URL to validate
 * @param allowedUrlPatterns - Array of glob-like patterns to match against
 * @returns boolean - True if the URL matches at least one pattern
 */
export const matchesAllowedUrlPattern = (url: string, allowedUrlPatterns?: string[]): boolean => {
	if (!allowedUrlPatterns || allowedUrlPatterns.length === 0) {
		return false;
	}

	const normalizedUrl = url.replace(/^https?:\/\//, '');
	return allowedUrlPatterns.some((pattern) => {
		const normalizedPattern = pattern.replace(/^https?:\/\//, '');
		const regex = patternToRegex(normalizedPattern);
		return regex.test(normalizedUrl);
	});
};

/**
 * Filters an array of URLs based on the validation configuration.
 * If allowAnyUrl is true, returns all URLs.
 * Otherwise, filters URLs by the allowed patterns.
 *
 * @param urls - Array of URLs to validate
 * @param config - URL validation configuration
 * @returns Filtered array of valid URLs
 */
export const validUrls = (urls: string[], config?: UrlValidationConfig): string[] => {
	if (!config) {
		return [];
	}

	if (config.allowAnyUrl) {
		return urls;
	}

	return urls.filter((url) => matchesAllowedUrlPattern(url, config.allowedUrlPatterns));
};
